import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import { SearchResult } from './UniversalSearch';
import { Theme } from 'helpers/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    padding: '8px 10px',
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.1s'
  },
  highlighted: {
    backgroundColor: '#f0f1f3',
    '& $tag': {
      background: 'white'
    }
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0
  },
  icon: {
    width: 26,
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: 10,
    color: theme.colors.secondaryGrey,
    fontSize: 14
  },
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gridGap: 5
  },
  title: {
    fontSize: theme.fontSizes.default,
    fontWeight: 500,
    color: theme.colors.primaryBlack
  },
  titleBadge: {
    fontSize: theme.fontSizes.default,
    fontWeight: 400,
    color: theme.colors.secondaryGrey,
    whiteSpace: 'nowrap'
  },
  subtitle: {
    fontSize: 12,
    fontWeight: 400,
    color: theme.colors.secondaryGrey,
    marginTop: 1
  },
  tag: {
    display: 'flex',
    alignItems: 'center',
    gridGap: 6,
    padding: '3px 8px',
    background: theme.colors.backgroundGrey,
    border: '1px solid #ededed',
    borderRadius: 6,
    flexShrink: 0,
    transition: 'background-color 0.1s'
  },
  tagLabel: {
    fontSize: 10,
    fontWeight: 650,
    letterSpacing: 0.5,
    color: theme.colors.secondaryGrey
  },
  tagValue: {
    fontSize: 11,
    fontWeight: 500,
    color: '#74777f',
    fontVariantNumeric: 'tabular-nums'
  }
}));

type Props = {
  result: SearchResult;
  icon: IconDefinition;
  onClick: (result: SearchResult) => void;
  highlighted?: boolean;
  onHover?: () => void;
};

function UserResult({ result, icon, onClick, highlighted, onHover }: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const rootRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!highlighted || !rootRef.current) return;
    const el = rootRef.current;
    const container = el.closest('[data-results-scroller]');
    if (!container) return;

    // Manual scroll instead of scrollIntoView: browsers don't reliably apply
    // scroll-margin with block 'nearest', and we want the section header
    // (above) and container padding (below) to stay visible around the row
    const marginTop = 36;
    const marginBottom = 10;
    const containerRect = container.getBoundingClientRect();
    const rowRect = el.getBoundingClientRect();

    if (rowRect.bottom + marginBottom > containerRect.bottom) {
      container.scrollTop += rowRect.bottom + marginBottom - containerRect.bottom;
    } else if (rowRect.top - marginTop < containerRect.top) {
      container.scrollTop -= containerRect.top - (rowRect.top - marginTop);
    }
  }, [highlighted]);

  return (
    <div
      ref={rootRef}
      className={classNames(classes.root, highlighted && classes.highlighted)}
      onMouseDown={() => {
        onClick(result);
      }}
      onMouseMove={onHover}
    >
      <div className={classes.left}>
        <div
          className={classes.icon}
          style={result.iconColor ? { color: result.iconColor } : undefined}
        >
          {result.avatar ? (
            <ProfileIcon
              name={result.avatar.name}
              url={result.avatar.url}
              size={26}
            />
          ) : (
            <FontAwesomeIcon icon={icon} />
          )}
        </div>
        <div>
          <div className={classes.titleRow}>
            <div className={classes.title}>{result.title}</div>
            {result.titleBadge && (
              <div className={classes.titleBadge}>- {result.titleBadge}</div>
            )}
          </div>
          {result.subtitle && (
            <div className={classes.subtitle}>{result.subtitle}</div>
          )}
        </div>
      </div>
      {result.tag && (
        <div className={classes.tag}>
          <div className={classes.tagLabel}>{result.tagLabel ?? 'TTC-ID'}</div>
          <div className={classes.tagValue}>{result.tag}</div>
        </div>
      )}
    </div>
  );
}

export default UserResult;
